import useOptimisticAddNewSiteAddressToProject from '@/api/mutations/project/optimistic/useOptimisticAddNewSiteAddressToProject';
import useOptimisticLinkSiteAddressToProject from '@/api/mutations/project/optimistic/useOptimisticLinkSiteAddressToProject';
import useOptimisticUnlinkSiteAddressToProject from '@/api/mutations/project/optimistic/useOptimisticUnlinkedSiteAddressFromProject';
import useOptimisticUpdateProjectAddress from '@/api/mutations/project/optimistic/useOptimisticUpdateProjectAddress';
import AddAddressModal, {
  IProps as IAddAddressModalProps,
} from '@/ui/components/addressModals/AddAddressModal';
import EditMultipleAddressesAndAddressContactsModal from '@/ui/components/addressModals/EditMultipleAddressesAndAddressContactsModal';
import SelectEditAddressAndAddressContactsModal, {
  IProps as ISelectEditAddressAndAddressContactsModalProps,
} from '@/ui/components/addressModals/SelectEditAddressAndAddressContactsModal';
import { Modal, ModalProps, SkeletonLoader } from '@payaca/ambrosia';

import { FC, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useGetProjectByIdOverview from '../../../../api/queries/project/useGetProjectByIdOverview';

export interface IProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  onDealUpdateSuccessLegacy?: () => void;
}

const SiteAddressModal: FC<IProps> = (props) => {
  const { isOpen, onClose, onDealUpdateSuccessLegacy } = props;

  const { projectId } = useParams<{ projectId: string }>();
  const { project, isLoading: isLoadingProjectOverview } =
    useGetProjectByIdOverview(projectId);

  const customerId = project?.customer?.id;
  const hasCustomer = !!customerId;

  /**
   * Mutations
   */
  const { mutate: linkSiteAddressToProjectMutation } =
    useOptimisticLinkSiteAddressToProject(projectId, {
      onSuccess: () => {
        onDealUpdateSuccessLegacy?.();
      },
    });
  const { mutate: updateAddressMutation } = useOptimisticUpdateProjectAddress(
    projectId,
    {
      onSuccess: () => {
        onDealUpdateSuccessLegacy?.();
      },
    }
  );
  const { mutate: addNewSiteAddressToProjectMutation } =
    useOptimisticAddNewSiteAddressToProject(projectId, {
      onSuccess: () => {
        onDealUpdateSuccessLegacy?.();
      },
    });
  const { mutate: unlinkedSiteAddressFromProjectMutation } =
    useOptimisticUnlinkSiteAddressToProject(projectId, {
      onSuccess: () => {
        onDealUpdateSuccessLegacy?.();
      },
    });

  const [modalScreen, setModalScreen] = useState<
    'customerAddress' | 'addAddress' | 'newAddressOnly' | 'multipleAddresses'
  >('newAddressOnly');

  useEffect(() => {
    switch (true) {
      case project?.addresses?.site?.length &&
        project.addresses.site.length > 1:
        // Project has multiple site addresses
        setModalScreen('multipleAddresses');
        break;
      case hasCustomer || project?.addresses.site.length === 1:
        // Project has a customer (with potentially addresses to choose from) or has one site address
        setModalScreen('customerAddress');
        break;
      default:
        setModalScreen('newAddressOnly');
    }
  }, [project, isOpen, hasCustomer]);

  /**
   * Callbacks
   */
  const handleAddressSelectChange = useCallback(
    (addressId: string) => {
      const currentSelectedAddress = project?.addresses.site[0];
      linkSiteAddressToProjectMutation({
        unlinkSiteAddressFromProjectInput: currentSelectedAddress
          ? {
              projectId: projectId,
              addressId: currentSelectedAddress.id,
            }
          : undefined,
        linkSiteAddressToProjectInput: {
          projectId: projectId,
          addressId: addressId,
        },
      });
    },
    [project, projectId, linkSiteAddressToProjectMutation]
  );

  const handleAddressChange = useCallback<
    Required<ISelectEditAddressAndAddressContactsModalProps>['onChange']
  >(
    (updatedAddress) => {
      const input = {
        ...updatedAddress,
      };

      delete input.fullLocalAddress;

      updateAddressMutation(input);
    },
    [updateAddressMutation]
  );

  const handleAddAddress = useCallback<
    Required<IAddAddressModalProps>['onSave']
  >(
    (updatedAddress) => {
      const selectedAddress = project?.addresses.site[0];

      addNewSiteAddressToProjectMutation({
        unlinkSiteAddressFromProjectInput: selectedAddress
          ? {
              projectId: projectId,
              addressId: selectedAddress.id,
            }
          : {
              projectId: projectId,
              addressId: '',
            },
        addNewSiteAddressToProjectInput: {
          projectId: projectId,
          linkAddressToProjectCustomer: true,
          address: updatedAddress,
        },
      });
    },
    [addNewSiteAddressToProjectMutation, project, projectId]
  );

  const handleUnlinkSiteAddressFromProject = useCallback(
    (addressId: string) => {
      unlinkedSiteAddressFromProjectMutation({
        projectId: projectId,
        addressId,
      });
    },
    [unlinkedSiteAddressFromProjectMutation, projectId]
  );

  if (!isOpen) {
    return null;
  }

  if (isLoadingProjectOverview) {
    return (
      <Modal title="Site Addresses" isOpen onClose={onClose}>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <SkeletonLoader.Title />
            <SkeletonLoader.Text className="w-1/4" />
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      <EditMultipleAddressesAndAddressContactsModal
        title="Site Addresses"
        isOpen={modalScreen === 'multipleAddresses'}
        onClose={onClose}
        addresses={project?.addresses.site || []}
        onChange={handleAddressChange}
        onRemoveAddress={handleUnlinkSiteAddressFromProject}
      />

      {customerId && (
        <SelectEditAddressAndAddressContactsModal
          title="Site Addresses"
          isOpen={modalScreen === 'customerAddress'}
          onClose={onClose}
          onDone={onClose}
          selectedAddress={project?.addresses.site[0]}
          customerId={customerId}
          onAddNewAddress={() => setModalScreen('addAddress')}
          onAddressChange={handleAddressSelectChange}
          onChange={handleAddressChange}
        />
      )}

      <AddAddressModal
        title="Add a new Customer Address"
        onBack={
          modalScreen !== 'newAddressOnly'
            ? () => setModalScreen('customerAddress')
            : undefined
        }
        isOpen={
          modalScreen === 'newAddressOnly' || modalScreen === 'addAddress'
        }
        onClose={
          modalScreen === 'newAddressOnly'
            ? onClose
            : () => setModalScreen('customerAddress')
        }
        onSave={handleAddAddress}
      />
    </>
  );
};

export default SiteAddressModal;
